import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import Logo from '../components/Logo'
import { supabase } from '../lib/supabase'
import { setAuthToken } from '../lib/authToken'
import { trackEvent } from '../api'

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 7,
  fontSize: 14, color: 'var(--text1)', background: 'var(--surface)', outline: 'none',
}

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [resetSent, setResetSent] = useState(false)

  const valid = email.includes('@') && password.length >= 6

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!valid) return
    setLoading(true)
    setError('')
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      setError(error.message)
      setLoading(false)
    } else if (data.session) {
      // Write token immediately — onAuthStateChange fires async (next tick),
      // too late for the first API calls the dashboard makes on mount.
      setAuthToken(data.session.access_token)
      trackEvent('user_login')
      // PublicOnlyRoute will redirect (respecting ?redirect=) once AuthContext picks up the session
    }
  }

  const handleForgotPassword = async () => {
    if (!email.includes('@')) {
      setError('Enter your email address first.')
      return
    }
    setLoading(true)
    setError('')
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/login`,
    })
    if (error) {
      setError(error.message)
    } else {
      setResetSent(true)
    }
    setLoading(false)
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ marginBottom: 32 }}>
        <Logo size="lg" linkTo="/" />
      </div>

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '40px 36px', width: '100%', maxWidth: 420, boxShadow: 'var(--shadow-lg)' }}>
        <div style={{ fontFamily: 'Sora, sans-serif', fontSize: 20, fontWeight: 700, color: 'var(--text1)', marginBottom: 6 }}>Welcome back</div>
        <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 28 }}>Sign in to manage your AI skills</div>

        <button
          onClick={() => supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: 'https://app.skilflow.ai' } })}
          style={{ width: '100%', padding: '10px 0', borderRadius: 7, fontSize: 14, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 10, background: '#fff', color: '#3c4043', border: '1px solid #dadce0' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 0 0 1 12c0 1.94.46 3.77 1.18 5.42l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </button>

        <button
          onClick={() => supabase.auth.signInWithOAuth({ provider: 'github', options: { redirectTo: 'https://app.skilflow.ai' } })}
          style={{ width: '100%', padding: '10px 0', borderRadius: 7, fontSize: 14, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 20, background: '#24292e', color: '#fff', border: 'none' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
            <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
          </svg>
          Continue with GitHub
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>or</span>
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        </div>

        {resetSent ? (
          <div style={{ fontSize: 13, color: 'var(--success-text)', background: 'var(--success-bg)', border: '1px solid var(--success-bg)', borderRadius: 7, padding: '12px 16px', marginBottom: 16 }}>
            Password reset email sent. Check your inbox.
          </div>
        ) : (
          <form onSubmit={handleSignIn}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text2)', marginBottom: 6 }}>Email</label>
              <input type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text2)', marginBottom: 6 }}>Password</label>
              <input type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} />
            </div>

            {error && (
              <div style={{ fontSize: 12, color: '#e53e3e', marginBottom: 12 }}>{error}</div>
            )}

            <button type="submit" disabled={!valid || loading} className="btn-primary" style={{ width: '100%', padding: '10px 0', fontSize: 14, opacity: (!valid || loading) ? 0.35 : 1 }}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: 'var(--text2)' }}>
          No account?{' '}
          <Link to="/signup" style={{ color: 'var(--brand)' }}>Create one</Link>
          {' '}·{' '}
          <button onClick={handleForgotPassword} disabled={loading} style={{ fontSize: 13, color: 'var(--brand)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>
            Forgot password?
          </button>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <a href="/privacy" style={{ fontSize: 11, color: 'var(--text3)', textDecoration: 'none' }}>Privacy Policy</a>
      </div>
    </div>
  )
}
